// ============================================================================
// Golden Touch Lash & Brow - central content + config
// Edit this file to update copy, images, links, portfolio and services.
// ============================================================================

// Production domain used for canonical URLs, sitemap and Open Graph.
// Change this to your live domain when deploying.
export const SITE_URL = "https://www.goldentouchlash.com";

export const BRAND = {
  name: "Golden Touch Lash and Brow",
  shortName: "Golden Touch",
  tagline: "Lash Extensions, Brows & Beauty in Vero Beach, FL",
  logo: "/images/logo.webp",
  city: "Vero Beach",
  region: "FL",
  postalCode: "32960",
  country: "US",
};

export const CONTACT = {
  phoneDisplay: "954-866-4799",
  // This number is TEXT ONLY (no calls). Use sms links everywhere.
  smsHref: `sms:+19548664799?&body=${encodeURIComponent(
    "Hi Golden Touch! I'd like to book an appointment."
  )}`,
  smsRescheduleHref: `sms:+19548664799?&body=${encodeURIComponent(
    "Hi Golden Touch! I'd like to reschedule my upcoming appointment."
  )}`,
  locationDisplay: "Vero Beach, FL 32960",
};

// Social profiles. Instagram + Google verified. TikTok/Facebook are best-guess
// handles matching the brand - please confirm/correct these two.
export const SOCIALS = {
  instagram: "https://www.instagram.com/Goldentouchlash",
  tiktok: "https://www.tiktok.com/@goldentouchlash",
  facebook: "https://www.facebook.com/goldentouchlashandbrow",
  googleReview: "https://g.page/r/CXZEa1AMlSc2EBM/review",
};

// Acuity Scheduling (Squarespace Scheduling) - resolved from goldentouchlash.as.me
export const ACUITY = {
  owner: "27912655",
  // Official embeddable scheduler. embed.js auto-resizes the iframe to content.
  embedSrc: "https://app.acuityscheduling.com/schedule.php?owner=27912655&ref=embedded_csp",
  // Direct external scheduling link (fallback / "open in new tab").
  directUrl: "https://goldentouchlash.as.me/schedule/d9f05906",
  // Individual service name -> Acuity appointmentType ID (deep-links to that exact
  // service on the scheduler). Pulled from the live Acuity account.
  serviceTypeIds: {
    // Lash — full sets
    "Classic Lash Extensions": "39578913",
    "Hybrid Lash Extensions": "40131165",
    "Volume Lash Extensions": "40131300",
    "Wispy Volume Extensions": "40131988",
    "Mega Volume": "40132295",
    "Strip Lash": "40134114",
    "Lash Clusters": "54512033",
    "Removal": "41238719",
    // Lash — fills
    "Classic Fill": "40299189",
    "Hybrid Fill": "40299208",
    "Volume Fill": "40299229",
    "Wispy Volume Fill": "40299265",
    "Mega Volume Fill": "40819635",
    "Mini Fill": "40299376",
    "Bottom Lashes": "40300070",
    // Brow
    "Brow Wax": "40299978",
    "Brow Threading": "40299994",
    "Brow Tint (No Wax)": "40300122",
    "Brow Lamination": "40300042",
    Consultation: "43935674",
    "Signature Microshading": "49624826",
    "Microshading Correction": "54513576",
    "Microshading Color Refresh": "96241903",
    // Skin & Beauty — facials
    "Deep Cleanse Signature Facial": "83100187",
    "Dermaplaning Cleanse": "83101820",
    "Refresh Facial": "86399023",
    "Full Face Threading with Cleanse": "96242171",
    // Skin & Beauty — body / waxing
    "Full Face Threading": "40420074",
    Vajacial: "54512554",
    "Brazilian Wax": "54520097",
    "Full Leg Wax": "54520131",
    "Half Leg Wax": "57714972",
    "Bikini Wax": "54523343",
    "Underarm Wax": "54523373",
    "Vajacial + Brazilian": "67012850",
    "Lip Wax": "70303024",
    "Chin Wax": "70303039",
    "Thong Wax": "73200732",
  },
  // All services now live in one of the three site categories in Acuity, so a
  // service's card category is enough. Per-service chips still use serviceTypeIds.
  serviceCategoryOverrides: {},
  // Acuity category holding all bookable training classes.
  // NOTE: must match the exact category name in Acuity (verified from the live
  // scheduler) or the deep-link is ignored and the generic chooser is shown.
  coursesCategory: "Courses ( Lash Extensions and Brows)",
  // Optional: per-service preselection. Add Acuity appointmentType IDs here to
  // deep-link individual services, e.g. appointmentType: "12345678".
};

// Shopify Storefront API - powers the /shop page (products + hosted checkout).
// The Storefront access token below is a PUBLIC token, designed to be used in
// the browser. It only allows read access + cart/checkout. Never place an Admin
// (shpat_...) token here.
export const SHOPIFY = {
  domain: "k1c1ye-je.myshopify.com",
  storefrontToken: "0e77c3443991c4f1a286d1132d56fd28",
  apiVersion: "2024-10",
};

export const NAV = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "Shop", to: "/shop" },
  { label: "Training", to: "/training" },
];

export const SERVICES = [
  {
    slug: "lash-extensions",
    title: "Lash Extensions",
    acuityCategory: "Lashes",
    tagline: "Full Sets \u2022 Fills \u2022 Lifts & Tints",
    options: [
      "Classic Lash Extensions",
      "Hybrid Lash Extensions",
      "Volume Lash Extensions",
      "Wispy Volume Extensions",
      "Mega Volume",
      "Strip Lash",
      "Lash Clusters",
      "Removal",
      "Classic Fill",
      "Hybrid Fill",
      "Volume Fill",
      "Wispy Volume Fill",
      "Mega Volume Fill",
      "Mini Fill",
      "Bottom Lashes",
    ],
    image: "/images/lashes.webp",
    work: "/images/work-lashes.webp",
    alt: "Close-up of dramatic volume eyelash extensions on a brown-eyed client",
    short:
      "Custom lash extensions designed to enhance your natural eye shape - from a soft, everyday classic set to bold, full volume.",
    intro:
      "Wake up effortlessly beautiful. Every set of lash extensions at Golden Touch is mapped and applied to complement your unique eye shape, lifestyle and desired look - lightweight, comfortable and long-wearing.",
    forWho:
      "Anyone who wants to save time on their morning routine, add length and fullness, or feel polished for a special occasion.",
    prep: [
      "Arrive with clean, makeup-free lashes.",
      "Avoid caffeine right before your appointment for a more relaxing experience.",
      "Remove contact lenses if they cause sensitivity.",
    ],
    aftercare: [
      "Keep lashes dry for the first 24\u201348 hours.",
      "Avoid oil-based products around the eyes.",
      "Brush gently with a clean spoolie and avoid rubbing.",
      "Book regular fills to maintain fullness.",
    ],
    faqs: [
      { q: "What is the difference between classic, hybrid and volume?", a: "Classic applies one extension per natural lash for a natural, mascara-like look. Volume uses multiple ultra-fine extensions per lash for a fuller, fluffier effect. Hybrid blends both for texture and dimension." },
      { q: "How do I choose the right style?", a: "During your appointment we map your lashes to your eye shape and discuss the look you want. If you are unsure, we will recommend a style that suits you." },
    ],
  },
  {
    slug: "brow-services",
    title: "Brow Services",
    acuityCategory: "Brow Services ",
    tagline: "Microshading \u2022 Brow Tint \u2022 Shape",
    options: [
      "Brow Wax",
      "Brow Threading",
      "Brow Wax & Tint",
      "Brow Tint (No Wax)",
      "Brow Lamination",
      "Consultation",
      "Microshading Perfecting Session",
      "Signature Microshading",
      "Microshading Correction",
      "Microshading Color Refresh",
    ],
    image: "/images/brows.webp",
    work: "/images/work-brows.webp",
    alt: "Perfectly shaped and laminated eyebrows on a client",
    short:
      "Fuller, fluffier, beautifully shaped brows through lamination, tinting and precision shaping tailored to your face.",
    intro:
      "Your brows frame your face. Our brow services lift, define and shape to create a fuller, more symmetrical look that lasts - personalised to your natural growth and facial features.",
    forWho:
      "Anyone wanting fuller-looking, well-groomed brows, more symmetry, or a low-maintenance shape that lasts for weeks.",
    prep: [
      "Come with clean brows, free of makeup or brow products.",
      "Avoid exfoliating the brow area for 24 hours before.",
      "Let us know about any recent facials or skin treatments.",
    ],
    aftercare: [
      "Keep brows dry for 24 hours after lamination.",
      "Avoid heavy sweating, steam and saunas for the first day.",
      "Apply any recommended nourishing oil to keep brows conditioned.",
      "Brush brows into shape daily.",
    ],
    faqs: [
      { q: "How long does brow lamination last?", a: "Results vary by individual, but many clients enjoy their laminated brows for several weeks with proper care." },
      { q: "Can I combine tint and shape with lamination?", a: "Yes - many clients combine services for the most defined, polished result. We will tailor a plan during your visit." },
    ],
  },
  {
    slug: "skin-beauty",
    title: "Skin & Beauty",
    acuityCategory: "Skin and beauty",
    tagline: "Facials \u2022 Body Waxing \u2022 Vajacials",
    options: [
      "Deep Cleanse Signature Facial",
      "Dermaplaning Cleanse",
      "Refresh Facial",
      "Full Face Threading with Cleanse",
      "Men's Facial",
      "Full Face Threading",
      "Vajacial",
      "Brazilian Wax",
      "Full Leg Wax",
      "Half Leg Wax",
      "Bikini Wax",
      "Underarm Wax",
      "Vajacial + Brazilian",
      "Lip Wax",
      "Chin Wax",
      "Thong Wax",
    ],
    image: "/images/skin.webp",
    work: "/images/work-skin.webp",
    alt: "Relaxed client receiving a luxury facial and skin treatment",
    short:
      "Radiant skin and finishing touches - facials, waxing and additional beauty services in a calm, luxurious setting.",
    intro:
      "Round out your beauty routine with our skin and beauty services. Relax and enjoy treatments designed to leave you feeling refreshed, smooth and glowing.",
    forWho:
      "Anyone looking to refresh their skin, keep it smooth, or add finishing beauty touches to their lash and brow appointment.",
    prep: [
      "Arrive with clean skin where possible.",
      "Avoid strong exfoliants or retinoids for a few days before waxing.",
      "Let us know of any allergies or sensitivities.",
    ],
    aftercare: [
      "Keep treated skin clean and moisturised.",
      "Avoid direct sun and heavy heat immediately after.",
      "Follow any product recommendations given at your appointment.",
    ],
    faqs: [
      { q: "Which skin and beauty services do you offer?", a: "We offer facials, waxing and additional beauty services. Contact us or check the booking page for the current menu." },
    ],
  },
];

// Bookable training classes - mirror of the "Lash and brow courses" Acuity category.
// Clients book these directly via /book?category=Lash and brow courses.
export const TRAINING_COURSES = [
  {
    name: "Classic Lash Class",
    level: "Beginner Friendly",
    duration: "8 hours",
    price: "$500",
    desc: "A full-day, hands-on beginner class covering lash mapping, isolation, application and safe practice to confidently start your lash journey.",
  },
  {
    name: "Wispy Volume Lash Class",
    level: "Certified lash techs only",
    duration: "3h 20m",
    price: "$800",
    desc: "An advanced course for experienced artists ready to master the signature wispy volume set with pro techniques and dimension.",
  },
  {
    name: "Refresher Class",
    level: "Experienced technicians",
    duration: "2h 30m",
    price: "$400",
    desc: "A hands-on refresher for trained artists who want to sharpen technique, rebuild confidence and get real-time feedback.",
  },
  {
    name: "Brow Tinting 101 - Hands-On Training",
    level: "Hands-on training",
    duration: "3h 20m",
    price: "$350",
    desc: "Learn the signature brow tinting method step by step - brow analysis, color selection and application for flawless, long-lasting brows.",
  },
  {
    name: "Brow Tinting & Lamination Class",
    level: "Certification",
    duration: "5 hours",
    price: "$550",
    desc: "Master signature brow tinting and lamination in a 5-hour hands-on class with live demo, guided practice and supplies for class use.",
  },
];

export const TRUST = [  { icon: "calendar", title: "Easy Online Booking", text: "Book in just a few clicks." },
  { icon: "chat", title: "Text Only Contact", text: "Text us anytime. We're here to help." },
  { icon: "shield", title: "Safe & Clean", text: "Your safety and satisfaction are our priority." },
  { icon: "sparkle", title: "Luxury Experience", text: "Relax and enjoy a premium beauty experience." },
];

// Real client work - the salon's own gallery photos, shown as a clean slideshow.
export const PORTFOLIO = [
  5, 7, 8, 12, 13, 14, 15,
  17, 18, 19, 20, 21, 22, 23, 24, 25, 26,
].map((n) => ({ src: `/images/gallery/g${n}.webp` }));

// Bold, always-visible booking policies. Verified from the client's Acuity page.
// Add more policy items here and they will render in the Policies section.
export const POLICIES = [
  {
    title: "Confirm Your Appointment",
    text: "Please check your email notification to confirm your appointment.",
  },
  {
    title: "Cancel or Reschedule",
    text: "You have up to 24 hours before your appointment to cancel or reschedule using the link in your email confirmation.",
  },
  {
    title: "Last-Minute Cancellations",
    text: "A 40% fee applies to last-minute cancellations.",
  },
  {
    title: "No-Call / No-Show",
    text: "A 50% fee applies to any no-call / no-show.",
  },
  {
    title: "Text Only",
    text: "Our business line is TEXT ONLY - no phone calls.",
  },
];

// Real client testimonials only. Leave empty until approved reviews are provided.
// Example item: { name: "Jane D.", text: "...", rating: 5 }
export const REVIEWS = [];

export const IMAGES = {
  hero: "/images/hero.webp",
  heroModel: "/images/hero-model.png",
  heroMobile: "/images/hero-mobile.webp",
  training: "/images/training.png",
  logo: "/images/logo.webp",
  owner: "/images/owner.webp",
  ownerTraining: "/images/owner-training.webp",
};

export const FALLBACK_IMG = "/images/hero.webp";
